import React, { useEffect, useMemo } from "react";

import { Paper, Tab, Tabs } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { Link, Route, Switch, useRouteMatch } from "react-router-dom";

import { DeploymentAction, fetchBillingRulesSettingsChanges } from "actions";

import WaitForSync from "components/LoadingSpinner/WaitForSync";
import { UserRoleSettings } from "components/UserRoles";

import { ApiModel } from "constants/loading";
import {
  DeploymentPermission,
  SaleyardPermission,
} from "constants/permissions";
import { SettingsTab } from "constants/settings";

import { Billing } from "containers/Settings/Billing";
import EditDeployment from "containers/Settings/Deployment";
import { NLISPanel } from "containers/Settings/NLIS";
import { StatusSettings } from "containers/Settings/Statuses";
import SystemSettings from "containers/Settings/System";

import { getSettingsRoute } from "lib/navigation";
import { hasPermission } from "lib/permissions";

import {
  getActiveSaleyardAdminSaleyard,
  getIsLivestockAgent,
  getIsRolePermissionsEnabled,
  getLivestockAgentHasNlisSaleyard,
  selectRoleDeployments,
} from "selectors";

import history from "appHistory";
import { useGetSelectedTabFromRoute } from "hooks/useGetSelectedTabFromRoute";
import {
  useHasBillingRunPermission,
  useHasDeploymentPermission,
  useHasExternalIntegrationsPermission,
  useHasManualChargesPermission,
  useHasSaleyardOrDeploymentPermission,
  useSomeHasPermission,
} from "hooks/useHasPermission";

import { AgeSettings } from "./Ages";
import { BreedSettings } from "./Breeds";
import { DentitionSettings } from "./Dentition";
import { IntegrationSettings } from "./Integrations";
import { LabelsSettings } from "./Labels/LabelsSettings";
import { MarkSettings } from "./Marks";
import { ProductSettings } from "./Products";
import { RuleBooks } from "./RuleBooks";
import { SexSettings } from "./Sexes";

const Settings = () => {
  const hasNlisPermission = useSelector(state =>
    getIsRolePermissionsEnabled(state)
      ? getLivestockAgentHasNlisSaleyard(state) ||
        hasPermission(
          getActiveSaleyardAdminSaleyard(state),
          SaleyardPermission.featureNlis,
        )
      : true,
  );

  const hasBreedsPermission = useSomeHasPermission(
    selectRoleDeployments,
    DeploymentPermission.featureBreeds,
  );

  const hasSexesPermission = useSomeHasPermission(
    selectRoleDeployments,
    DeploymentPermission.featureSexes,
  );

  const hasAgesPermission = useSomeHasPermission(
    selectRoleDeployments,
    DeploymentPermission.featureAges,
  );

  const hasProductsPermission = useSomeHasPermission(
    selectRoleDeployments,
    DeploymentPermission.featureProducts,
  );

  const hasLabelsPermission = useSomeHasPermission(
    selectRoleDeployments,
    DeploymentPermission.featureLabels,
  );

  const hasMarksPermission = useSomeHasPermission(
    selectRoleDeployments,
    DeploymentPermission.featureMarks,
  );

  const hasUserManagementPermission = useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureUserManagement,
    SaleyardPermission.featureUserManagement,
  );

  const hasExternalIntegrationPermission =
    useHasExternalIntegrationsPermission();

  const hasRuleBookPermission = useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureRuleBook,
    SaleyardPermission.featureRuleBook,
  );

  // ref LedgerAccount Modal - uses featureBillingRun
  const hasLedgerAccountsPermission = useHasBillingRunPermission();

  const hasManualChargesPermission = useHasManualChargesPermission;

  const hasSettingBillingTabPermission =
    hasLedgerAccountsPermission && hasManualChargesPermission;

  const hasDeploymentUpdatePermission = useHasDeploymentPermission(
    DeploymentPermission.featureEditDeployment,
  );

  const isLivestockAgent = useSelector(getIsLivestockAgent);

  const hasDentitionPermission = useHasDeploymentPermission(
    DeploymentPermission.featureDentition,
  );

  const settingsOptions = useMemo(
    () =>
      [
        hasNlisPermission && {
          label: "NLIS",
          component: NLISPanel,
        },
        hasBreedsPermission && {
          label: "Breeds",
          component: BreedSettings,
        },
        hasSexesPermission && {
          label: "Sexes",
          component: SexSettings,
        },
        hasAgesPermission && {
          label: "Ages",
          component: AgeSettings,
        },
        hasProductsPermission && {
          label: "Products",
          component: ProductSettings,
        },
        hasLabelsPermission && {
          label: "Labels",
          component: LabelsSettings,
        },
        isLivestockAgent && {
          label: "Statuses",
          component: StatusSettings,
        },
        hasMarksPermission && {
          label: "Marks",
          component: MarkSettings,
        },
        hasUserManagementPermission && {
          label: "Users",
          component: UserRoleSettings,
        },
        hasExternalIntegrationPermission && {
          label: "Integrations",
          component: IntegrationSettings,
        },
        hasRuleBookPermission && {
          label: "Billing Rules",
          component: RuleBooks,
        },
        hasSettingBillingTabPermission && {
          label: "Billing",
          component: Billing,
        },
        hasDeploymentUpdatePermission && {
          label: "Agency",
          component: EditDeployment,
        },
        hasDentitionPermission && {
          label: "Dentition",
          component: DentitionSettings,
        },
        {
          label: "System",
          component: SystemSettings,
        },
      ].filter(Boolean),
    [
      hasAgesPermission,
      hasBreedsPermission,
      hasDentitionPermission,
      hasDeploymentUpdatePermission,
      hasExternalIntegrationPermission,
      hasLabelsPermission,
      hasMarksPermission,
      hasNlisPermission,
      hasProductsPermission,
      hasRuleBookPermission,
      hasSettingBillingTabPermission,
      isLivestockAgent,
      hasSexesPermission,
      hasUserManagementPermission,
    ],
  );
  const match = useRouteMatch();
  const tabMap = ({ label }) => (
    <Tab
      label={label}
      key={`tab-${label}`}
      value={label}
      data-tour={label}
      component={Link}
      to={`${match.path}${label}/`}
    />
  );

  const currentTab = useGetSelectedTabFromRoute(settingsOptions);
  const routeMap = ({ label, component }) => (
    <Route path={`${match.path}${label}/`} component={component} key={label} />
  );

  useEffect(() => {
    // If we aren't on a tab, open the first.
    if (!currentTab && settingsOptions[0]?.label) {
      history.push(getSettingsRoute(settingsOptions[0].label));
    }
  }, [currentTab, match.path, settingsOptions]);

  const dispatch = useDispatch();
  useEffect(() => {
    let fetchSettingsAction = null;
    switch (currentTab) {
      case SettingsTab.billingRules:
        fetchSettingsAction = fetchBillingRulesSettingsChanges;
        break;
      case SettingsTab.agency:
        fetchSettingsAction = DeploymentAction.requestChanges;
        break;
      default:
        break;
    }
    fetchSettingsAction && dispatch(fetchSettingsAction());
  }, [dispatch, currentTab]);

  return (
    <>
      <Paper>
        <Tabs value={currentTab} variant="scrollable">
          {settingsOptions.map(tabMap)}
        </Tabs>
      </Paper>

      <Switch>{settingsOptions.map(routeMap)}</Switch>
    </>
  );
};

const SettingsLoadingWrapper = () => (
  <WaitForSync requiredData={[ApiModel.AGENCIES, ApiModel.DEPLOYMENTS]}>
    <Settings />
  </WaitForSync>
);

export default SettingsLoadingWrapper;
